/* eslint-disable */
/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* eslint-disable no-unused-vars */
import {helpers} from 'commerce-sdk-isomorphic'
import {getAppOrigin, getMrtOrigin} from 'coreUtils/url';
import {HTTPError} from 'coreUtils/adapters/sdk';
import {createCodeVerifier, generateCodeChallenge} from './pkce';
import {createGetTokenBody, isTokenValid, parseSlasJWT} from './utils';
import {fetch, isInIframe, isServer} from 'coreUtils/utils';
import Cookies from 'js-cookie';

import {ECustomerAuthType} from 'coreClasses/customer/ICustomer';

/**
 * An object containing the customer's login credentials.
 * @typedef {Object} CustomerCredentials
 * @property {string} credentials.email
 * @property {string} credentials.password
 */

/**
 * Salesforce Customer object.
 * {@link https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customer}}
 * @typedef {Object} Customer
 */

const usidStorageKey = 'usid';
const encUserIdStorageKey = 'enc-user-id';
const refreshTokenRegisteredStorageKey = 'cc-nx';
const refreshTokenGuestStorageKey = 'cc-nx-g';
const sessionGuardStorageKey = 'cc-sg';
const tokenStorageKey = 'cc-at';
const dwSessionIdKey = 'dwsid';
const dwDntKey = 'dw_dnt';
const dwAnonymousStorageKeyPrefix = 'dwanonymous_';
const REFRESH_TOKEN_COOKIE_AGE = 90; // 90 days. This value matches SLAS cartridge.
const EXPIRED_TOKEN = 'EXPIRED_TOKEN';
const INVALID_TOKEN = 'invalid refresh_token';
const firstCqcidStorageKey = 'firstCqcid';
const slasCallbackEndpoint = '/callback';
const externalProfileProviderId = 'external-profile-providerId';
const externalProfileExternalId = 'external-profile-externalId';
const externalProfileFirstName = 'external-profile-firstname';
const externalProfileLastName = 'external-profile-lastname';
const externalProfileEmail = 'external-profile-email';
const SSR_STORAGE = new Map();
const ONE_DAY = 86400000;
const CONTEXT_LIFETIME = ONE_DAY;

const getIncrementalKey = (key, num) => (num > 1 ? `${key}_${num}` : key);

/**
 * A  class that provides auth functionality for the retail react app.
 */

class Auth {
	constructor(api, managerRepository, siteId, organizationId) {
		this._managerRepository = managerRepository;
		this._api = api;
		this._siteId = siteId;
		this._organizationId = organizationId;
		this._onClient = !isServer;
		this._storages = new Map();
		this._sessionStorage = this._onClient ? new SessionStorage() : this.getStorage();
		this.login = this.login.bind(this);
		this.logout = this.logout.bind(this);
		this._refreshAccessToken = this._refreshAccessToken.bind(this);
	}

	/**
	 * Enum for user types
	 * @enum {string}
	 */
	static USER_TYPE = {
		REGISTERED: 'registered',
		GUEST: 'guest',
	};

	get authToken() {
		let token = '';
		let i = 1;

		do {
			token += this.getStorage().get(tokenStorageKey, i) || '';

			i++;
		} while (this.getStorage().get(tokenStorageKey, i));

		return token ? `Bearer ${token}` : null;
	}

	setAuthToken(token, expires) {
		this.deleteAuthToken();

		if (token) {
			let options;

			if (expires) {
				options = {expires};
			}

			this.getStorage().set(tokenStorageKey, token, options);
		}
	}

	deleteAuthToken() {
		let i = 1;

		while (this.getStorage().get(tokenStorageKey, i)) {
			this.getStorage().delete(tokenStorageKey, i);

			i++;
		}
	}

	get userType() {
		return this.getStorage().get(refreshTokenRegisteredStorageKey)
			? Auth.USER_TYPE.REGISTERED
			: Auth.USER_TYPE.GUEST;
	}

	get refreshToken() {
		const storageKey =
			this.userType === Auth.USER_TYPE.REGISTERED
				? refreshTokenRegisteredStorageKey
				: refreshTokenGuestStorageKey;

		return this.getStorage().get(storageKey);
	}

	get sessionGuard() {
		return this.getStorage().get(sessionGuardStorageKey);
	}

	get usid() {
		return this.getStorage().get(usidStorageKey);
	}

	set usid(usid) {
		this.getStorage().set(usidStorageKey, usid);
	}

	get dwanonymous() {
		const allData = this.getGlobalStorage().get();

		for (const key in allData) {
			if (Object.hasOwnProperty.call(allData, key) && key.startsWith(dwAnonymousStorageKeyPrefix)) {
				return allData[key];
			}
		}

		return null;
	}

	get encUserId() {
		return this.getStorage().get(encUserIdStorageKey);
	}

	set encUserId(encUserId) {
		this.getStorage().set(encUserIdStorageKey, encUserId);
	}

	/**
	 * Returns the global store
	 * @return {Storage}
	 */
	getGlobalStorage() {
		if (!this._onClient) {
			return SSR_STORAGE;
		}

		const key = 'global_storage';

		if (!this._storages.has(key)) {
			this._storages.set(key, new CookieStorage());
		}

		return this._storages.get(key);
	}

	/**
	 * Returns the customer-list based store
	 * @return {Storage}
	 */
	getStorage() {
		/**
		 * On CSR we want to save the token for different sites
		 * While on SSR, since we set the context once, we need it to be saved on country base
		 */
		const cacheKey = !this._onClient
			? this._managerRepository
					.getLocalesMgr()
					.getCurrentCountry()
					?.getCountryCode()
			: this._siteId;

		if (!this._storages?.has(cacheKey)) {
			if (this._onClient && !isInIframe()) {
				this._storages.set(cacheKey, new CookieStorage(cacheKey));
			} else {
				this._storages.set(cacheKey, new ServerStorage(cacheKey));
			}
		}

		return this._storages.get(cacheKey);
	}

	getFirstCqcid() {
		return this.getGlobalStorage().get(firstCqcidStorageKey);
	}

	setFirstCqcid(cqcid) {
		this.getGlobalStorage().set(firstCqcidStorageKey, cqcid);
	}

	/**
	 * Save refresh token in designated storage.
	 *
	 * @param {string} token The refresh token.
	 * @param {USER_TYPE} type Type of the user.
	 */
	_saveRefreshToken(token, type) {
		if (type === Auth.USER_TYPE.REGISTERED) {
			this.getStorage().set(refreshTokenRegisteredStorageKey, token, {
				expires: REFRESH_TOKEN_COOKIE_AGE,
			});
			this.getStorage().delete(refreshTokenGuestStorageKey);

			return;
		}

		this.getStorage().set(refreshTokenGuestStorageKey, token, {expires: REFRESH_TOKEN_COOKIE_AGE});
		this.getStorage().delete(refreshTokenRegisteredStorageKey);
	}

	/**
	 * Called with the details from the redirect page that _loginWithCredentials returns
	 * I think it's best we leave it to developers on how and where to call from
	 * @param {{grantType, code, usid, codeVerifier, redirectUri}} requestDetails - The cutomerId of customer to get.
	 */
	async getLoggedInToken(requestDetails) {
		const data = new URLSearchParams();
		const {grantType, code, usid, codeVerifier, redirectUri} = requestDetails;
		data.append('code', code);
		data.append('grant_type', grantType);
		data.append('usid', usid);
		data.append('code_verifier', codeVerifier);
		data.append('client_id', this._api._config.parameters.clientId);
		data.append('redirect_uri', redirectUri);
		data.append('channel_id', this._siteId);

		const options = {
			headers: {
				'Content-Type': `application/x-www-form-urlencoded`,
			},
			body: data,
		};
		const response = await this._api.shopperLogin.getAccessToken(options);

		// Check for error response before handling the token
		if (response.status_code) {
			throw new HTTPError(response.status_code, response.message);
		}
		this._handleShopperLoginTokenResponse(response);

		return response;
	}

	/**
	 * Get Social login url to redirect users to
	 *
	 * The redirect_uri should match the one configured for the idp through the SLAS Admin API
	 * The hint parameter should be one of 'Google', 'Facebook', 'Salesforce', etc
	 *
	 * @param {string} identityProvider - The name of the identity provider
	 * @returns {Promise}
	 */
	async getSocialLoginURL(identityProvider) {
		const codeVerifier = createCodeVerifier();
		const codeChallenge = await generateCodeChallenge(codeVerifier);

		if (this._onClient) {
			this._sessionStorage.set('codeVerifier', codeVerifier);
		}

		const url = new URL(
			`${getAppOrigin()}${this._api._config.proxyPath}/shopper/auth/v1/organizations/${this._organizationId}` +
				`/oauth2/authorize`,
		);

		url.searchParams.append('redirect_uri', `${getAppOrigin()}/oauthCallback`);
		url.searchParams.append('client_id', this._api._config.parameters.clientId);
		url.searchParams.append('code_challenge', codeChallenge);
		url.searchParams.append('channel_id', this._siteId);
		url.searchParams.append('response_type', 'code');
		url.searchParams.append('hint', identityProvider);

		return url.toString();
	}

	/**
	 * Complete social login flow by getting as access token for the customer
	 *
	 * @param {usid} usid - The usid generated by SLAS
	 * @param {code} code - The authentication code generated by the identity provider
	 * @returns {Promise}
	 */
	async getSocialLoginToken(usid, code) {
		const codeVerifier = this._sessionStorage.get('codeVerifier');

		return await this.getLoggedInToken({
			usid,
			code,
			grantType: 'authorization_code_pkce',
			codeVerifier,
			redirectUri: `${getAppOrigin()}/oauthCallback`,
		});
	}

	/**
	 * Checks if the current auth token is valid
	 * @return {boolean}
	 */
	isAuthTokenValid() {
		return isTokenValid(this.authToken);
	}

	/**
	 * Authorizes the customer as a registered or guest user.
	 * @param {CustomerCredentials} [credentials]
	 * @param {Object} tokenData - Token data
	 * @returns {Promise}
	 */
	async login(credentials, tokenData = null) {
		// Calling login while its already pending will return a reference
		// to the existing promise.
		if (this._pendingLogin) {
			// in case we didn't finish one login and have another login with another
			// credentials, wait till first finish and make new
			if (this._pendingLoginCredentials !== credentials) {
				return this._pendingLogin.then((loginResult) => {
					return this.login(credentials);
				});
			}

			return this._pendingLogin;
		}
		let retries = 0;

		const startLoginFlow = () => {
			let authorizationMethod = '_loginAsGuest';

			if (isServer && !this.isAuthTokenValid()) {
				authorizationMethod = '_loginServer';
			} else if (credentials?.type === ECustomerAuthType.AGENT) {
				authorizationMethod = '_loginOnBehalf';
			} else if (credentials?.providerId) {
				authorizationMethod = '_loginIdentityProvider';
			} else if (credentials) {
				authorizationMethod = '_loginWithCredentials';
			} else if (this.isAuthTokenValid()) {
				authorizationMethod = '_reuseCurrentLogin';
			} else if (this.refreshToken) {
				if (
					this.getStorage().get(externalProfileProviderId) &&
					this.getStorage().get(externalProfileExternalId)
				) {
					authorizationMethod = '_refreshIdentityProviderAccessToken';
				} else {
					authorizationMethod = '_refreshAccessToken';
				}
			}

			return this[authorizationMethod](credentials, tokenData).catch((error) => {
				const retryErrors = [INVALID_TOKEN, EXPIRED_TOKEN];

				if (retries === 0 && retryErrors.includes(error.message)) {
					retries = 1; // we only retry once
					return this._clearAuth(startLoginFlow());
				}
				throw error;
			});
		};

		this._pendingLogin = startLoginFlow().finally(() => {
			// When the promise is resolved, we need to remove the reference so
			// that subsequent calls to `login` can proceed.
			this._pendingLogin = undefined;
			this._pendingLoginCredentials = undefined;
		});
		this._pendingLoginCredentials = credentials;

		return this._pendingLogin;
	}

	/**
	 * Clears the stored auth token and optionally logs back in as guest.
	 * @param {boolean} [shouldLoginAsGuest=true] - Indicates if we should automatically log back in as a guest
	 * @returns {(Promise<Customer>|undefined)}
	 */
	async logout(shouldLoginAsGuest = true) {
		const options = {
			parameters: {
				refresh_token: this.refreshToken,
				client_id: this._api._config.parameters.clientId,
				channel_id: this._siteId,
			},
		};

		try {
			await this._api.shopperLogin.logoutCustomer(options, true);
		} catch (e) {}

		await this._clearAuth();

		if (shouldLoginAsGuest) {
			return this.login();
		}
	}

	/**
	 * Handles Response from ShopperLogin GetAccessToken, calls the getCustomer method and removes the PCKE code verifier from session storage
	 * @private
	 * @param {object} tokenResponse - access_token,id_token,refresh_token, expires_in,token_type, usid, customer_id, enc_user_id, idp_access_token
	 */
	_handleShopperLoginTokenResponse(tokenResponse) {
		const {access_token, refresh_token, usid, enc_user_id, id_token, expires_in} = tokenResponse;
		this.setAuthToken(access_token, expires_in);
		this.usid = usid;

		// we use id_token to distinguish guest and registered users
		if (id_token && id_token.length > 0) {
			this.encUserId = enc_user_id;
			refresh_token && this._saveRefreshToken(refresh_token, Auth.USER_TYPE.REGISTERED);
		} else {
			refresh_token && this._saveRefreshToken(refresh_token, Auth.USER_TYPE.GUEST);
		}

		if (this._onClient) {
			this._sessionStorage.delete('codeVerifier');
		}
	}

	async _reuseCurrentLogin() {
		const parsedToken = parseSlasJWT(this.authToken);

		const customer = {
			authType: this.userType,
			customerId: parsedToken.customerId,
		};

		return customer;
	}

	/**
	 * Begins oAuth PCKE Flow
	 * @param {{email, password}}} credentials - User Credentials.
	 * @returns {object} - a skeleton registered customer object that can be used to retrieve a complete customer object
	 */
	async _loginWithCredentials(credentials) {
		if (this._api._config.useSLASPrivateClient) {
			try {
				const dnt = this.getStorage().get(dwDntKey);

				const response = await helpers.loginRegisteredUserB2C(
					this._api.shopperLogin,
					{
						username: credentials.email,
						password: credentials.password,
						clientSecret: '_PLACEHOLDER_PROXY-PWA_KIT_SLAS_CLIENT_SECRET'
					},
					{
						redirectURI: `${getAppOrigin()}${slasCallbackEndpoint}`,
						...(this.usid && {usid: this.usid}),
						...(dnt && {dnt: dnt})
					}
				)

				this._handleShopperLoginTokenResponse(response);
				return {
					customerId: response.customer_id,
					authType: Auth.USER_TYPE.REGISTERED,
				};
			} catch (error) {
				const json = await error.response.json();
				throw new HTTPError(error.response.status, json.message);
			}
		} else {
			const codeVerifier = createCodeVerifier();
			const codeChallenge = await generateCodeChallenge(codeVerifier);

			this._sessionStorage.set('codeVerifier', codeVerifier);

			const authorization = `Basic ${btoa(`${credentials.email}:${credentials.password}`)}`;
			const options = {
				headers: {
					Authorization: authorization,
					'Content-Type': `application/x-www-form-urlencoded`,
				},
				body: {
					redirect_uri: `${getAppOrigin()}${slasCallbackEndpoint}`,
					client_id: this._api._config.parameters.clientId,
					code_challenge: codeChallenge,
					channel_id: this._siteId,
					usid: this.usid, // mergeBasket API requires guest usid to be sent in the authToken
				},
			};
			const response = await this._api.shopperLogin.authenticateCustomer(options, true);

			if (response.status >= 400) {
				const json = await response.json();
				throw new HTTPError(response.status, json.message);
			}

			const tokenBody = createGetTokenBody(
				response.url,
				`${getAppOrigin()}${slasCallbackEndpoint}`,
				this._sessionStorage.get('codeVerifier'),
			);
			const {customer_id} = await this.getLoggedInToken(tokenBody);

			return {
				customerId: customer_id,
				authType: Auth.USER_TYPE.REGISTERED,
			};
		}
	}

	async _loginOnBehalf(credentials) {
		const response = await this._managerRepository.getSfccAPI().getLoginOnBehalfToken(credentials.email);

		this._handleShopperLoginTokenResponse(response);

		return {
			authType: ECustomerAuthType.AGENT,
			customerId: response.customer_id,
		};
	}

	/**
	 * Cached login for SSR only
	 * @param {CustomerCredentials} [credentials]
	 * @param {Object} tokenData - token data
	 * @returns {Promise<{needsCommerceContext: boolean, customerId: (string|string|*), authType: (Auth.USER_TYPE|string)}>}
	 * @private
	 */
	async _loginServer(credentials, tokenData) {
		const now = Date.now();
		let response;

		if (isTokenValid(tokenData?.access_token)) {
			response = tokenData;
		} else {
			response = await fetch(
				`${getMrtOrigin()}/mobify/login-server?channel_id=${this._api._config.parameters.siteId}&countryCode=${this._managerRepository.getLocalesMgr().getCurrentCountry().getCountryCode()}`,
				{
					headers: {
						now: now.toString(),
					},
				},
			).then((res) => res.json());
		}

		this._handleShopperLoginTokenResponse(response);
		const creationTime = parseInt(response.creationTimestamp);

		return {
			authType: Auth.USER_TYPE.GUEST,
			customerId: response.customer_id,
			needsCommerceContext: creationTime === now || creationTime + CONTEXT_LIFETIME >= now,
		};
	}

	/**
	 * Begins oAuth PCKE Flow whith no credentials
	 * @returns {object} - a guest customer object
	 */
	async _loginAsGuest() {
		if (this._api._config.useSLASPrivateClient) {
			const params = {};
			const dnt = this.getStorage().get(dwDntKey);

			if (this.usid) {
				params.usid = this.usid;
			}

			if (dnt) {
				params.dnt = dnt;
			}

			const authResponse = await helpers.loginGuestUserPrivate(
				this._api.shopperLogin,
				params,
				{clientSecret: '_PLACEHOLDER_PROXY-PWA_KIT_SLAS_CLIENT_SECRET'}
			);

			this._handleShopperLoginTokenResponse(authResponse);
			return {
				authType: Auth.USER_TYPE.GUEST,
				customerId: authResponse.customer_id,
				needsCommerceContext: true,
			};
		} else {
			const codeVerifier = createCodeVerifier();
			const codeChallenge = await generateCodeChallenge(codeVerifier);

			this._sessionStorage.set('codeVerifier', codeVerifier);

			const options = {
				headers: {
					Authorization: '',
					'Content-Type': `application/x-www-form-urlencoded`,
				},
				parameters: {
					redirect_uri: `${getAppOrigin()}${slasCallbackEndpoint}`,
					client_id: this._api._config.parameters.clientId,
					code_challenge: codeChallenge,
					response_type: 'code',
					hint: 'guest',
				},
			};
			const response = await this._api.shopperLogin.authorizeCustomer(options, true);

			if (response.status >= 400) {
				let text = await response.text();
				let errorMessage = text;

				try {
					const data = JSON.parse(text);

					if (data.message) {
						errorMessage = data.message;
					}
				} catch {} // eslint-disable-line no-empty
				throw new HTTPError(response.status, errorMessage);
			}

			const tokenBody = createGetTokenBody(
				response.url,
				`${getAppOrigin()}${slasCallbackEndpoint}`,
				this._sessionStorage.get('codeVerifier'),
			);
			const {customer_id} = await this.getLoggedInToken(tokenBody);

			// A guest customerId will never return a customer from the customer endpoint
			return {
				authType: Auth.USER_TYPE.GUEST,
				customerId: customer_id,
				needsCommerceContext: true,
			};
		}
	}

	/**
	 * Refreshes Logged In Token
	 * @private
	 * @returns {<Promise>} - Handle Shopper Login Promise
	 */
	async _refreshAccessToken() {
		const data = new URLSearchParams();
		data.append('grant_type', 'refresh_token');
		data.append('refresh_token', this.refreshToken);
		data.append('client_id', this._api._config.parameters.clientId);

		const options = {
			headers: {
				'Content-Type': `application/x-www-form-urlencoded`,
			},
			body: data,
		};
		const response = await this._api.shopperLogin.getAccessToken(options);

		// Check for error response before handling the token
		if (response.status_code) {
			throw new HTTPError(response.status_code, response.message);
		}
		this._handleShopperLoginTokenResponse(response);

		const {id_token, enc_user_id, customer_id} = response;
		let customer = {
			authType: Auth.USER_TYPE.GUEST,
			customerId: customer_id,
		};

		// Determining if registered customer or guest
		if (id_token.length > 0 && enc_user_id.length > 0) {
			customer.authType = Auth.USER_TYPE.REGISTERED;
		}

		return customer;
	}

	/**
	 * Begins social login authentication
	 * @param {IIdentityProviderCredentials} providerCredentials - body object with provider tokens
	 * @returns {object} - a skeleton registered customer object that can be used to retrieve a complete customer object
	 */
	async _loginIdentityProvider(providerCredentials) {
		const params = {...providerCredentials};

		if (this.usid) {
			params.usid = this.usid;
		}

		const sfccAuthData = await this._managerRepository.getSfccAPI().identityProviderLogin(params);

		if (!sfccAuthData?.success) {
			const error = new HTTPError(400, sfccAuthData.errorType);

			if (sfccAuthData?.crmProfile) {
				error.crmProfile = sfccAuthData.crmProfile;
			}

			throw error;
		}

		if (!sfccAuthData?.slas_auth) {
			throw new HTTPError(500, 'Something went wrong during identity provider login');
		}

		this._handleShopperLoginTokenResponse(sfccAuthData.slas_auth);
		const customer = {
			authType: Auth.USER_TYPE.REGISTERED,
			customerId: sfccAuthData.slas_auth.customer_id,
			isSessionBridged: true
		};

		this.getStorage().set(externalProfileProviderId, sfccAuthData.externalProfile.providerId);
		this.getStorage().set(externalProfileExternalId, sfccAuthData.externalProfile.customerId);

		return customer;
	}

	/**
	 * Refreshes Identity Provider Logged In Token
	 * @private
	 * @returns {<Promise>} - Handle Identity Provider Login Promise
	 */
	async _refreshIdentityProviderAccessToken() {
		const sfccAuthData = await this._managerRepository.getSfccAPI().refreshIdentityProviderLogin({
			providerId: this.getStorage().get(externalProfileProviderId),
			externalId: this.getStorage().get(externalProfileExternalId),
			refreshToken: this.refreshToken
		});

		if (!sfccAuthData?.success) {
			throw new HTTPError(400, sfccAuthData.errorType);
		}

		const slasAuth = sfccAuthData?.slas_auth ? sfccAuthData.slas_auth : null;

		if (!slasAuth) {
			throw new HTTPError(500, 'Something went wrong refreshing identity provider login');
		}

		this._handleShopperLoginTokenResponse(slasAuth);

		return {
			isSessionBridged: true,
			authType: Auth.USER_TYPE.REGISTERED,
			customerId: slasAuth.customer_id
		};
	}

	/**
	 * Begins social login authentication
	 * @param {IIdentityProviderCredentials} providerCredentials - body object with provider tokens
	 * @returns {object} - a skeleton registered customer object that can be used to retrieve a complete customer object
	 */
	async authenticateProviderLogin(providerCredentials) {
		const params = {...providerCredentials};

		if (this.usid) {
			params.usid = this.usid;
		}

		const sfccAuthData = await this._managerRepository.getSfccAPI().authenticateProviderLogin(params);

		if (!sfccAuthData.externalProfile) {
			return null;
		}

		this.getStorage().set(externalProfileProviderId, sfccAuthData.externalProfile.providerId);
		this.getStorage().set(externalProfileExternalId, sfccAuthData.externalProfile.customerId);
		this.getStorage().set(externalProfileFirstName, sfccAuthData.externalProfile.firstName);
		this.getStorage().set(externalProfileLastName, sfccAuthData.externalProfile.lastName);
		this.getStorage().set(externalProfileEmail, sfccAuthData.externalProfile.email);

		if (!sfccAuthData?.customer) {
			return {
				externalProfile: true,
			};
		}

		if (!sfccAuthData?.success) {
			throw new HTTPError(400, sfccAuthData.errorType);
		}

		if (!sfccAuthData?.slas_auth) {
			throw new HTTPError(500, 'Something went wrong during identity provider login');
		}

		this._handleShopperLoginTokenResponse(sfccAuthData.slas_auth);
		const customer = {
			authType: Auth.USER_TYPE.REGISTERED,
			customerId: sfccAuthData.slas_auth.customer_id,
		};

		return customer;
	}

	/**
	 * Register external profile into SFCC
	 * @param {object} profileData - body object with form data
	 * @returns {object} - a skeleton registered customer object that can be used to retrieve a complete customer object
	 */
	async registerExternalProfile(profileData) {
		const params = {
			...this._getExternalProfileData(),
			...profileData,
			systemProviderId: localStorage.getItem('identityProviderName'),
		};

		const sfccAuthData = await this._managerRepository.getSfccAPI().registerExternalProfile(params);

		if (!sfccAuthData?.success || !sfccAuthData?.customer) {
			throw new HTTPError(400, sfccAuthData.errorType);
		}

		if (!sfccAuthData?.slas_auth) {
			throw new HTTPError(500, 'Something went wrong during identity provider login');
		}

		this._handleShopperLoginTokenResponse(sfccAuthData.slas_auth);
		const customer = {
			authType: Auth.USER_TYPE.REGISTERED,
			customerId: sfccAuthData.slas_auth.customer_id,
		};

		return customer;
	}

	/**
	 * Get current external login data for registration
	 * @returns {object} - a customer object to be used for registration
	 */
	_getExternalProfileData() {
		const customerData = {
			providerId: this.getStorage().get(externalProfileProviderId),
			customerId: this.getStorage().get(externalProfileExternalId),
			firstName: this.getStorage().get(externalProfileFirstName),
			lastName: this.getStorage().get(externalProfileLastName),
			email: this.getStorage().get(externalProfileEmail),
		};

		this.getStorage().delete(externalProfileProviderId);
		this.getStorage().delete(externalProfileExternalId);
		this.getStorage().delete(externalProfileFirstName);
		this.getStorage().delete(externalProfileLastName);
		this.getStorage().delete(externalProfileEmail);

		return customerData;
	}

	/**
	 * Clears SFCC session cookies
	 */
	async clearSFCCSession() {
		/**
		 * since in some cases we are not using OCAPI session bridge anymore
		 * the dwsid cookie may be httponly and the only way to delete it is via HTTP call.
		 */
		await fetch(`${getAppOrigin()}/mobify/logout`);
		this.getGlobalStorage().delete(dwSessionIdKey);
		this.getGlobalStorage().delete(dwDntKey);
	}

	/**
	 * Removes the stored auth token.
	 */
	async _clearAuth() {
		await this.clearSFCCSession();
		this.deleteAuthToken();
		this.getStorage().delete(refreshTokenRegisteredStorageKey);
		this.getStorage().delete(refreshTokenGuestStorageKey);
		this.getStorage().delete(usidStorageKey);
		this.getStorage().delete(encUserIdStorageKey);
		this.getStorage().delete(externalProfileProviderId);
		this.getStorage().delete(externalProfileExternalId);
		this.getStorage().delete(externalProfileFirstName);
		this.getStorage().delete(externalProfileLastName);
		this.getStorage().delete(externalProfileEmail);
	}
}

export default Auth;

class Storage {
	set() {}

	get() {}

	delete() {}
}

class ServerStorage extends Storage {
	constructor(appendKey) {
		super();

		this._appendKey = appendKey ? `_${appendKey}` : '';
	}

	set(key, value, options, i) {
		SSR_STORAGE.set(getIncrementalKey(getIncrementalKey(key + this._appendKey, i), i), value);
	}

	get(key, i) {
		return typeof key === 'undefined' ? SSR_STORAGE.get() : SSR_STORAGE.get(getIncrementalKey(key + this._appendKey, i));
	}

	delete(key, i) {
		SSR_STORAGE.delete(getIncrementalKey(key + this._appendKey, i));
	}
}

class CookieStorage extends Storage {
	constructor(appendKey) {
		super();

		this._appendKey = appendKey ? `_${appendKey}` : '';

		if (typeof document === 'undefined') {
			throw new Error('CookieStorage is not avaliable on the current environment.');
		}
	}

	set(key, value, options, i) {
		Cookies.set(getIncrementalKey(key + this._appendKey, i), value, {secure: true, ...options});
	}

	get(key, i) {
		return typeof key === 'undefined' ? Cookies.get() : Cookies.get(getIncrementalKey(key + this._appendKey, i));
	}

	delete(key, i) {
		if (Cookies.get(getIncrementalKey(key + this._appendKey, i))) {
			Cookies.remove(getIncrementalKey(key + this._appendKey, i));
		}
	}
}

class SessionStorage extends Storage {
	constructor(...args) {
		super(args);

		if (typeof document === 'undefined') {
			throw new Error('SessionStorage is not avaliable on the current environment.');
		}
	}

	set(key, value) {
		sessionStorage.setItem(key, value);
	}

	get(key) {
		return sessionStorage.getItem(key);
	}

	delete(key) {
		sessionStorage.removeItem(key);
	}
}

class LocalStorage extends Storage {
	constructor() {
		super();

		if (typeof document === 'undefined') {
			throw new Error('LocalStorage is not available on the current environment.');
		}
	}

	set(key, value) {
		localStorage.setItem(key, value);
	}

	get(key) {
		return localStorage.getItem(key);
	}

	delete(key) {
		localStorage.removeItem(key);
	}
}
